import React, { useState, useEffect } from 'react';
import { getCourts, getMatches, generateMatchForCourt, endMatch } from '../services/api';
import type { Court, Match } from '../services/api';
import eventBus from '../utils/eventBus';
import { Button, CircularProgress, Box, Typography, GridLegacy as Grid, Paper, Alert } from '@mui/material';
import { useNotification } from '../context/NotificationContext'; // Import the hook

const CourtView: React.FC = () => {
  const [courts, setCourts] = useState<Court[]>([]);
  const [activeMatches, setActiveMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingCourtId, setLoadingCourtId] = useState<number | null>(null);
  const { showNotification } = useNotification(); // Use the hook

  const fetchData = async () => {
    try {
      setLoading(true);
      const [courtsData, matchesData] = await Promise.all([
        getCourts(),
        getMatches(true)
      ]);
      setCourts(courtsData);
      setActiveMatches(matchesData);
      setError(null);
    } catch (_err) {
      console.error("Failed to fetch court or match data", _err);
      setError("Failed to load court and match data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // The event bus is now handled by more specific listeners in other components,
    // so this component no longer needs to re-fetch on every data change.
  }, []);

  const handleGenerateMatch = async (courtId: number) => {
    setLoadingCourtId(courtId);
    try {
      const newMatch = await generateMatchForCourt(courtId);
      // Optimistically update this component's state
      setActiveMatches(prevMatches => [...prevMatches, newMatch]);
      // Dispatch event with payload for other components
      eventBus.dispatch('playersStatusChanged', { players: newMatch.players, newStatus: 'on_court' });
      showNotification('成功自動排點！', 'success');
    } catch (_err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (_err as any)?.response?.data?.detail || (_err as Error)?.message || 'Unknown error';
      showNotification(`自動排點時發生錯誤: ${errorMessage}`, 'error');
    } finally {
      setLoadingCourtId(null);
    }
  };

  const handleEndMatch = async (matchToEnd: Match) => {
    if (window.confirm('確定要結束這場對戰嗎？')) {
      try {
        // Optimistically update this component's state
        setActiveMatches(prevMatches => prevMatches.filter(match => match.id !== matchToEnd.id));
        
        await endMatch(matchToEnd.id);

        // Dispatch event with payload for other components
        eventBus.dispatch('playersStatusChanged', { players: matchToEnd.players, newStatus: 'available' });
        showNotification('比賽已成功結束。', 'success');
      } catch (_err: unknown) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (_err as any)?.response?.data?.detail || (_err as Error)?.message || 'Unknown error';
        showNotification(`結束對戰時發生錯誤: ${errorMessage}`, 'error');
        // If there's an error, re-fetch to revert the optimistic update
        fetchData();
      }
    }
  };
  
  const matchMap = new Map(Array.isArray(activeMatches) ? activeMatches.map(m => [m.court.id, m]) : []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        場地狀態
      </Typography>
      <Grid container spacing={2}>
        {Array.isArray(courts) && courts.map(court => {
          const match = matchMap.get(court.id);
          return (
            <Grid item key={court.id} xs={12} sm={6} md={4}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  borderRadius: '12px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" component="h3">{court.name}</Typography>
                  {match && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      LIVE
                    </Typography>
                  )}
                </Box>
                
                {match ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>A隊：</strong> {match.players.filter(p => match.team_a.includes(p.id)).map(p => p.name).join(' & ')}</Typography>
                    <Typography variant="body2"><strong>B隊：</strong> {match.players.filter(p => match.team_b.includes(p.id)).map(p => p.name).join(' & ')}</Typography>
                    <Button variant="contained" color="error" onClick={() => handleEndMatch(match)} fullWidth sx={{ mt: 2 }}>
                      結束對戰
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1, minHeight: '95px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>場地空閒中</Typography>
                    {loadingCourtId === court.id ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Button variant="contained" color="primary" onClick={() => handleGenerateMatch(court.id)} fullWidth>
                        自動排點
                      </Button>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CourtView;